import { useEffect, useMemo } from 'react';
import TanoProjo from '../Projection/TanoProjo';
import FeuxArtifice from '../Projection/FeuxArtifice';

const STYLE_ID = 'podium-3d-styles';

// Styles CSS complexes (3D, pseudo-éléments, keyframes) — définis hors composant
const PODIUM_STYLES = `
  @keyframes podFadeIn { from { opacity:0; transform:translateY(-10px); } to { opacity:1; transform:translateY(0); } }
  @keyframes podPlayerIn { from { opacity:0; transform:translateY(16px) scale(0.85); } to { opacity:1; transform:translateY(0) scale(1); } }
  @keyframes podRise { from { opacity:0; transform:rotateX(5deg) rotateY(-7deg) translateY(40px); } to { opacity:1; transform:rotateX(5deg) rotateY(-7deg) translateY(0); } }
  @keyframes podPulse { 0%,100% { opacity:0.25; transform:scale(1); } 50% { opacity:0.6; transform:scale(1.12); } }
  @keyframes podSparkle { 0%,100% { opacity:0; transform:translateY(0) scale(.5); } 30% { opacity:1; transform:translateY(-10px) scale(1); } 70% { opacity:.6; transform:translateY(-18px) scale(.7); } }
  @keyframes podRays { 0%,100% { opacity:.35; } 50% { opacity:1; } }

  .pod-trophy { width:54px; height:54px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-size:25px; margin-bottom:6px; position:relative; }
  .pod-trophy::after { content:''; position:absolute; inset:-5px; border-radius:50%; animation:podPulse 2.5s ease-in-out infinite; }
  .pod-trophy.gold { background:radial-gradient(circle at 38% 30%,#ffe57f,#e8a20e); box-shadow:0 0 22px rgba(255,213,79,0.5),0 0 50px rgba(232,162,14,0.2); }
  .pod-trophy.gold::after { background:radial-gradient(circle,rgba(255,213,79,0.3),transparent 70%); }
  .pod-trophy.silver { background:radial-gradient(circle at 38% 30%,#b0bcc8,#5a6a7a); box-shadow:0 0 16px rgba(100,120,140,0.4); }
  .pod-trophy.silver::after { background:radial-gradient(circle,rgba(100,120,140,0.2),transparent 70%); }
  .pod-trophy.bronze { background:radial-gradient(circle at 38% 30%,#ffc490,#b85a20); box-shadow:0 0 16px rgba(197,106,43,0.4); }
  .pod-trophy.bronze::after { background:radial-gradient(circle,rgba(197,106,43,0.2),transparent 70%); }

  .pod-block { width:140px; transform-style:preserve-3d; transform:rotateX(5deg) rotateY(-7deg); }
  .pod-f { position:absolute; width:140px; overflow:hidden; }
  .pod-front { display:flex; align-items:center; justify-content:center; transform:translateZ(22px); border-radius:8px 8px 0 0; }
  .pod-top { height:44px; top:0; transform-origin:top center; transform:rotateX(90deg); border-radius:8px 8px 0 0; }
  .pod-right { width:44px; right:-22px; top:0; transform-origin:left center; transform:rotateY(90deg); border-radius:0 8px 0 0; }

  .pod-bnum { font-size:68px; font-weight:900; user-select:none; position:relative; z-index:1; letter-spacing:-2px; }

  /* Gold block */
  .pod-block.gold .pod-front { height:220px; background:radial-gradient(ellipse at 30% 15%,rgba(255,245,180,0.6) 0%,transparent 45%),radial-gradient(ellipse at 50% 50%,rgba(255,220,100,0.3) 0%,transparent 50%),linear-gradient(180deg,#FFD740 0%,#FFC107 15%,#E8A000 30%,#C68500 50%,#A06A00 70%,#7A5000 85%,#5C3D00 100%); box-shadow:inset 0 2px 0 rgba(255,255,255,0.4),inset 0 -4px 15px rgba(0,0,0,0.3); }
  .pod-block.gold .pod-top { background:radial-gradient(ellipse at 40% 40%,rgba(255,250,200,0.5) 0%,transparent 60%),linear-gradient(180deg,#FFE680 0%,#FFD740 40%,#E8A800 100%); box-shadow:inset 0 0 15px rgba(255,255,255,0.25); }
  .pod-block.gold .pod-right { height:220px; background:radial-gradient(ellipse at 30% 20%,rgba(255,220,120,0.25) 0%,transparent 50%),linear-gradient(180deg,#C68500 0%,#9A6800 30%,#7A5200 55%,#5C3D00 80%,#3D2800 100%); box-shadow:inset 0 -8px 20px rgba(0,0,0,0.4); }
  .pod-block.gold .pod-bnum { color:rgba(120,80,0,0.55); text-shadow:0 -2px 1px rgba(255,230,130,0.6),0 2px 2px rgba(40,25,0,0.7),0 1px 4px rgba(0,0,0,0.4),0 0 20px rgba(255,200,50,0.15); }

  /* Silver block */
  .pod-block.silver .pod-front { height:160px; background:radial-gradient(ellipse at 30% 12%,rgba(200,210,225,0.5) 0%,transparent 45%),radial-gradient(ellipse at 55% 48%,rgba(150,165,185,0.25) 0%,transparent 50%),linear-gradient(180deg,#8898AA 0%,#728498 15%,#5C7088 30%,#4A5E72 50%,#384C60 70%,#283A4C 85%,#1A2A38 100%); box-shadow:inset 0 2px 0 rgba(255,255,255,0.3),inset 0 -4px 15px rgba(0,0,0,0.3); }
  .pod-block.silver .pod-top { background:radial-gradient(ellipse at 40% 40%,rgba(190,205,225,0.4) 0%,transparent 55%),linear-gradient(180deg,#9AAABE 0%,#7A8EA5 40%,#5A7088 100%); box-shadow:inset 0 0 15px rgba(255,255,255,0.15); }
  .pod-block.silver .pod-right { height:160px; background:radial-gradient(ellipse at 30% 20%,rgba(120,140,165,0.2) 0%,transparent 50%),linear-gradient(180deg,#485A6C 0%,#384A5C 30%,#283A4A 55%,#1C2C3A 80%,#101C28 100%); box-shadow:inset 0 -8px 20px rgba(0,0,0,0.4); }
  .pod-block.silver .pod-bnum { color:rgba(30,45,60,0.6); text-shadow:0 -2px 1px rgba(180,200,225,0.55),0 2px 2px rgba(10,18,28,0.7),0 1px 4px rgba(0,0,0,0.4),0 0 20px rgba(120,150,190,0.12); }

  /* Bronze block */
  .pod-block.bronze .pod-front { height:120px; background:radial-gradient(ellipse at 30% 15%,rgba(255,210,160,0.55) 0%,transparent 45%),radial-gradient(ellipse at 50% 50%,rgba(230,150,80,0.25) 0%,transparent 50%),linear-gradient(180deg,#F0A050 0%,#D88540 15%,#C06E28 30%,#A05518 50%,#804010 70%,#602E08 85%,#442004 100%); box-shadow:inset 0 2px 0 rgba(255,255,255,0.3),inset 0 -4px 15px rgba(0,0,0,0.3); }
  .pod-block.bronze .pod-top { background:radial-gradient(ellipse at 40% 40%,rgba(255,220,180,0.4) 0%,transparent 55%),linear-gradient(180deg,#FFC088 0%,#D08040 40%,#A86020 100%); box-shadow:inset 0 0 12px rgba(255,255,255,0.15); }
  .pod-block.bronze .pod-right { height:120px; background:radial-gradient(ellipse at 30% 20%,rgba(220,150,80,0.2) 0%,transparent 50%),linear-gradient(180deg,#8A4818 0%,#6A3510 30%,#502808 55%,#3A1C06 80%,#241204 100%); box-shadow:inset 0 -8px 20px rgba(0,0,0,0.4); }
  .pod-block.bronze .pod-bnum { color:rgba(80,35,5,0.6); text-shadow:0 -2px 1px rgba(255,200,140,0.55),0 2px 2px rgba(30,12,0,0.7),0 1px 4px rgba(0,0,0,0.4),0 0 20px rgba(220,130,50,0.12); }

  .pod-sparkle { position:absolute; width:4px; height:4px; background:#ffd54f; border-radius:50%; animation:podSparkle 2s ease-in-out infinite; }
  .pod-rays { position:absolute; top:-45px; left:50%; transform:translateX(-50%); width:200px; height:120px; background:radial-gradient(ellipse at 50% 95%,rgba(255,213,79,0.1) 0%,transparent 55%); pointer-events:none; animation:podRays 3s ease-in-out infinite; }

  .pod-ground-glow { position:absolute; bottom:-28px; left:50%; transform:translateX(-50%); border-radius:50%; filter:blur(14px); pointer-events:none; z-index:-1; }
  .pod-ground-glow.gold { width:200px; height:50px; background:radial-gradient(ellipse,rgba(255,195,30,0.5) 0%,rgba(200,140,0,0.1) 60%,transparent 80%); filter:blur(18px); }
  .pod-ground-glow.silver { width:170px; height:40px; background:radial-gradient(ellipse,rgba(100,120,150,0.3) 0%,rgba(70,90,110,0.06) 60%,transparent 80%); }
  .pod-ground-glow.bronze { width:160px; height:36px; background:radial-gradient(ellipse,rgba(210,120,50,0.35) 0%,rgba(160,80,30,0.08) 60%,transparent 80%); }

  .pod-contact-shadow { position:absolute; bottom:-8px; left:50%; transform:translateX(-50%); width:150px; height:16px; background:radial-gradient(ellipse,rgba(0,0,0,0.5) 0%,rgba(0,0,0,0.15) 60%,transparent 85%); filter:blur(4px); border-radius:50%; z-index:-1; }
`;

const SPARKLE_POSITIONS = [
  { left: '5%', top: '20%', delay: '0s' },
  { left: '78%', top: '8%', delay: '0.5s' },
  { left: '40%', top: '48%', delay: '1s' },
  { left: '88%', top: '42%', delay: '1.3s', size: 3 },
  { left: '15%', top: '62%', delay: '0.7s', size: 3 },
  { left: '60%', top: '68%', delay: '1.6s', size: 2 },
];

const MEDAL_MAP = { gold: '🏆', silver: '🏆', bronze: '🏆' };
const BLOCK_HEIGHTS = { gold: 220, silver: 160, bronze: 120 };
const ANIM_DELAYS = { gold: 0.05, silver: 0.35, bronze: 0.6 };
const PLAYER_DELAYS = { gold: 0.2, silver: 0.55, bronze: 0.8 };
const DOT_CLASSES = { gold: 'bg-[#ffd54f] shadow-[0_0_6px_rgba(255,213,79,0.5)]', silver: 'bg-[#7888a0] shadow-[0_0_6px_rgba(120,136,160,0.4)]', bronze: 'bg-[#c56a2b] shadow-[0_0_6px_rgba(197,106,43,0.4)]' };

const TIER_CONFIG = [
  { rank: 2, tier: 'silver' },
  { rank: 1, tier: 'gold' },
  { rank: 3, tier: 'bronze' },
];

const PodiumColumn = ({ entries, tier, rank }) => {
  if (!entries || entries.length === 0) return null;
  const isExAequo = entries.length > 1;
  const firstEntry = entries[0];

  return (
    <div className="flex flex-col items-center">
      {/* Joueur(s) */}
      <div
        className="flex flex-col items-center mb-3.5 relative"
        style={{ animation: `podPlayerIn 0.6s cubic-bezier(0.22,1,0.36,1) ${PLAYER_DELAYS[tier]}s both` }}
      >
        {/* Effets 1er uniquement */}
        {tier === 'gold' && (
          <>
            <div className="pod-rays" />
            <div className="absolute -top-[65px] left-1/2 -translate-x-1/2 w-[90px] h-[75px] pointer-events-none">
              {SPARKLE_POSITIONS.map((sp, i) => (
                <div
                  key={i}
                  className="pod-sparkle"
                  style={{
                    left: sp.left, top: sp.top,
                    animationDelay: sp.delay,
                    width: sp.size || 4, height: sp.size || 4,
                  }}
                />
              ))}
            </div>
          </>
        )}
        <div className={`pod-trophy ${tier}`}>{MEDAL_MAP[tier]}</div>
        <div className="flex flex-col items-center gap-0.5">
          {entries.slice(0, 3).map(e => (
            <div key={e.nom} className="text-sm font-extrabold text-white" style={{ textShadow: '0 2px 10px rgba(0,0,0,0.5)' }}>
              {e.nom}
            </div>
          ))}
          {entries.length > 3 && (
            <div className="text-xs font-semibold text-white/50">
              et {entries.length - 3} autres
            </div>
          )}
        </div>
        <div className="font-['Quicksand'] text-xs font-semibold text-white/50">
          {firstEntry.score}/{firstEntry.total}
        </div>
        {isExAequo && (
          <div className="mt-1 px-2 py-0.5 rounded-full bg-white/[0.1] text-[10px] font-semibold text-white/60">
            ex aequo
          </div>
        )}
      </div>

      {/* Bloc 3D */}
      <div className="relative">
        <div
          className={`pod-block ${tier}`}
          style={{
            height: BLOCK_HEIGHTS[tier],
            animation: `podRise 0.7s cubic-bezier(0.22,1,0.36,1) ${ANIM_DELAYS[tier]}s both`,
          }}
        >
          <div className={`pod-f pod-front ${tier}`}>
            <span className="pod-bnum">{rank}</span>
          </div>
          <div className={`pod-f pod-top ${tier}`} />
          <div className={`pod-f pod-right ${tier}`} />
        </div>
        <div className="pod-contact-shadow" />
        <div className={`pod-ground-glow ${tier}`} />
      </div>
    </div>
  );
};

/** Tano mascotte de coin — danse en boucle douce sur les écrans de fin. */
const TanoFete = () => (
  <div className="fixed bottom-6 right-8 z-[3] pointer-events-none">
    <TanoProjo variant="fete" mood="joy" size={130} />
  </div>
);

/**
 * Feu d'artifice de fin : 6 tirs étalés sur ~6 s au-dessus du podium,
 * one-shot (pas de boucle — les regards reviennent au classement).
 */
const BurstFinal = () => <FeuxArtifice />;

const AllTiedScreen = ({ entries }) => {
  const firstEntry = entries[0];

  return (
    <div
      className="min-h-screen w-full flex flex-col items-center justify-center font-['Nunito'] overflow-hidden relative"
      style={{ background: 'radial-gradient(ellipse at 50% 85%, #1a3a8a 0%, #0d1f4d 40%, #070e24 100%)' }}
    >
      <BurstFinal />
      <TanoFete />
      <div className="flex flex-col items-center [zoom:0.6] sm:[zoom:0.75] md:[zoom:0.9] lg:[zoom:1] xl:[zoom:1.1] 2xl:[zoom:1.25]">
        <div
          className="font-['Quicksand'] text-base font-bold text-white/55 tracking-wider uppercase mb-10 z-[2]"
          style={{ animation: 'podFadeIn 0.6s ease both' }}
        >
          🏆 TanoEd Live — Classement
        </div>

        <div
          className="flex flex-col items-center relative"
          style={{ animation: 'podPlayerIn 0.6s cubic-bezier(0.22,1,0.36,1) 0.2s both' }}
        >
          <div className="pod-rays" />
          <div className="absolute -top-[65px] left-1/2 -translate-x-1/2 w-[90px] h-[75px] pointer-events-none">
            {SPARKLE_POSITIONS.map((sp, i) => (
              <div
                key={i}
                className="pod-sparkle"
                style={{
                  left: sp.left, top: sp.top,
                  animationDelay: sp.delay,
                  width: sp.size || 4, height: sp.size || 4,
                }}
              />
            ))}
          </div>
          <div className="pod-trophy gold text-4xl">🏆</div>
        </div>

        <p
          className="text-4xl font-extrabold text-white mt-6 mb-2"
          style={{ animation: 'podFadeIn 0.6s 0.4s ease both', textShadow: '0 2px 20px rgba(255,213,79,0.3)' }}
        >
          Tout le monde est premier !
        </p>
        <p
          className="font-['Quicksand'] text-lg font-semibold text-white/50 mb-8"
          style={{ animation: 'podFadeIn 0.6s 0.6s ease both' }}
        >
          {entries.length} participants — {firstEntry.score}/{firstEntry.total}
        </p>

        <div
          className="flex flex-wrap justify-center gap-1.5 max-w-[600px] max-h-[28vh] overflow-y-auto px-4"
          style={{ animation: 'podFadeIn 0.8s 0.8s ease both' }}
        >
          {entries.map(e => (
            <div
              key={e.nom}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/[0.06] border border-white/[0.08]"
            >
              <div className={`w-2 h-2 rounded-full ${DOT_CLASSES.gold}`} />
              <span className="text-[11px] font-bold text-white/80">{e.nom}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const PodiumQCM = ({ podiumRanks }) => {
  // Injection CSS une seule fois
  useEffect(() => {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = PODIUM_STYLES;
    document.head.appendChild(style);
    return () => { if (style.parentNode) style.parentNode.removeChild(style); };
  }, []);

  const rankMap = useMemo(() => {
    const m = new Map();
    podiumRanks.forEach(g => m.set(g.rank, g.entries));
    return m;
  }, [podiumRanks]);

  const totalParticipants = podiumRanks.reduce((sum, g) => sum + g.entries.length, 0);
  const allTied = podiumRanks.length === 1 && totalParticipants > 1;

  if (allTied) return <AllTiedScreen entries={podiumRanks[0].entries} />;

  const columns = TIER_CONFIG
    .filter(({ rank }) => rankMap.has(rank))
    .map(({ rank, tier }) => ({ entries: rankMap.get(rank), tier, rank }));

  // Ordre visuel : silver | gold | bronze
  return (
    <div
      className="min-h-screen w-full flex flex-col items-center justify-center font-['Nunito'] overflow-hidden relative"
      style={{ background: 'radial-gradient(ellipse at 50% 85%, #1a3a8a 0%, #0d1f4d 40%, #070e24 100%)' }}
    >
      {/* Sol */}
      <div
        className="fixed bottom-0 left-0 right-0 h-[250px] pointer-events-none z-0"
        style={{ background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.4) 50%, rgba(0,0,0,0.7) 100%)' }}
      />

      <BurstFinal />
      <TanoFete />

      {/* Contenu centré + responsive scaling */}
      <div className="flex flex-col items-center [zoom:0.6] sm:[zoom:0.75] md:[zoom:0.9] lg:[zoom:1] xl:[zoom:1.1] 2xl:[zoom:1.25]">
        {/* Titre */}
        <div
          className="font-['Quicksand'] text-base font-bold text-white/55 tracking-wider uppercase mb-10 z-[2]"
          style={{ animation: 'podFadeIn 0.6s ease both' }}
        >
          🏆 TanoEd Live — Classement
        </div>

        {/* Podium */}
        <div className="relative z-[2] mb-0">
          {/* Ombre du socle */}
          <div
            className="absolute -bottom-2.5 left-1/2 -translate-x-1/2 w-[560px] h-[70px] z-0"
            style={{ background: 'radial-gradient(ellipse, rgba(0,0,0,0.65) 0%, rgba(0,0,0,0.2) 55%, transparent 80%)', filter: 'blur(16px)' }}
          />
          <div className="flex items-end justify-center gap-2.5 relative z-[1]">
            {columns.map(({ entries, tier, rank }) => (
              <PodiumColumn key={tier} entries={entries} tier={tier} rank={rank} />
            ))}
          </div>
        </div>

        {/* Scoreboard */}
        <div
          className="flex flex-wrap justify-center gap-1.5 z-[3] mt-10 mb-8 max-h-[28vh] overflow-y-auto px-4"
          style={{ animation: 'podFadeIn 0.8s 1.1s ease both' }}
        >
          {columns
            .sort((a, b) => a.rank - b.rank)
            .flatMap(({ entries, tier }) =>
              entries.map(entry => (
                <div
                  key={`${tier}-${entry.nom}`}
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/[0.06] border border-white/[0.08]"
                >
                  <div className={`w-2 h-2 rounded-full ${DOT_CLASSES[tier]}`} />
                  <span className="text-[11px] font-bold text-white/80">{entry.nom}</span>
                  <span className="font-['Quicksand'] text-[10px] font-bold text-white/45">
                    {entry.score}/{entry.total}
                  </span>
                </div>
              ))
            )}
        </div>
      </div>
    </div>
  );
};

export default PodiumQCM;
